/**
 * @file Contains helpers to handle wall access settings.
 * */
import { __ } from '@@/bits/intl'
import { LibraryType, WallAccessPrivacyType, WallAccessRight } from 'enums'

// Classes are defined in surface_panel_share.vue
const WALL_ACCESS_RIGHT_COLOR_CLASS = {
  [WallAccessRight.Read]: 'readable',
  [WallAccessRight.Edit]: 'editable',
  [WallAccessRight.Write]: 'writable',
  [WallAccessRight.Administer]: 'adminable',
}

const WALL_ACCESS_RIGHT_TO_INTEGER_MAP = {
  [WallAccessRight.None]: 0,
  [WallAccessRight.Read]: 1,
  [WallAccessRight.Write]: 2,
  [WallAccessRight.Edit]: 4,
  [WallAccessRight.Administer]: 8,
}

const WALL_ACCESS_RIGHT_MAP = {
  [WallAccessRight.Read]: {
    permission: WallAccessRight.Read,
    descriptor: __('Can read'),
    description: __(
      "Can view posts. Cannot add posts, edit and approve others' posts, modify and delete padlet, invite collaborators.",
    ),
  },
  [WallAccessRight.Write]: {
    permission: WallAccessRight.Write,
    descriptor: __('Can write'),
    description: __(
      "Can view and add posts. Cannot edit and approve others' posts, modify and delete padlet, invite collaborators.",
    ),
  },
  [WallAccessRight.Edit]: {
    permission: WallAccessRight.Edit,
    descriptor: __('Can edit'),
    description: __(
      "Can view and add posts, edit and approve others' posts. Cannot modify and delete padlet, invite collaborators.",
    ),
  },
  [WallAccessRight.Administer]: {
    permission: WallAccessRight.Administer,
    descriptor: __('Can administer'),
    description: __(
      "Can view and add posts, edit and approve others' posts, modify and delete padlet, invite collaborators.",
    ),
  },
}

function getWallAccessPrivacyTypeText({ organization }: { organization: string }): {
  [key in WallAccessPrivacyType]: string
} {
  return {
    [WallAccessPrivacyType.Public]: __('Let the whole world see this padlet. Put it on my profile.'),
    [WallAccessPrivacyType.Secret]: __(
      'Keep the padlet hidden from the public. If I choose to share it with someone, they should be able to access it.',
    ),
    [WallAccessPrivacyType.Private]: __(
      'Keep the padlet hidden from the public. Even if someone manages to get to it, they should not be able to access it.',
    ),
    [WallAccessPrivacyType.OrgWideUnlisted]: __(
      'Keep the padlet hidden from the public. Let anyone from %{organization} access this padlet.',
      { organization },
    ),
    [WallAccessPrivacyType.OrgWideListed]: __(
      'Keep the padlet hidden from the public. Let anyone from %{organization} access this padlet.',
      { organization },
    ),
    [WallAccessPrivacyType.PasswordProtected]: __(
      'Keep the padlet hidden from the public. If I choose to share it with someone, they should require a password to access it.',
    ),
    [WallAccessPrivacyType.LoggedInUsersOnly]: __(
      'Keep the padlet hidden from the public. If I choose to share it with someone, they should be able to access it.',
    ),
    [WallAccessPrivacyType.LibraryWideUnlisted]: __(
      'Keep the padlet hidden from the public. Let anyone from %{organization} access this padlet.',
      { organization },
    ),
    [WallAccessPrivacyType.LibraryWideListed]: __(
      'Keep the padlet hidden from the public. Let anyone from %{organization} access this padlet.',
      { organization },
    ),
  }
}

// Long names should match services/rails/app/services/wall_access_manager.rb#human_readable
function getWallAccessPrivacyTypeName({
  libraryType = LibraryType.Team,
  length = 'short',
}: {
  libraryType: LibraryType | undefined
  length: 'long' | 'short'
}): {
  [key in WallAccessPrivacyType]: string
} {
  return {
    [LibraryType.Team]: {
      short: {
        [WallAccessPrivacyType.Public]: __('Public'),
        [WallAccessPrivacyType.Secret]: __('Secret'),
        [WallAccessPrivacyType.Private]: __('Private'),
        [WallAccessPrivacyType.OrgWideUnlisted]: __('Org only'),
        [WallAccessPrivacyType.OrgWideListed]: __('Org only'),
        [WallAccessPrivacyType.PasswordProtected]: __('Password'),
        [WallAccessPrivacyType.LoggedInUsersOnly]: __('Secret'),
        [WallAccessPrivacyType.LibraryWideUnlisted]: __('Team only'),
        [WallAccessPrivacyType.LibraryWideListed]: __('Team only'),
      },
      long: {
        [WallAccessPrivacyType.Public]: __('Public'),
        [WallAccessPrivacyType.Secret]: __('Secret'),
        [WallAccessPrivacyType.Private]: __('Private'),
        [WallAccessPrivacyType.OrgWideUnlisted]: __('Organization only'),
        [WallAccessPrivacyType.OrgWideListed]: __('Organization only'),
        [WallAccessPrivacyType.PasswordProtected]: __('Password protected'),
        [WallAccessPrivacyType.LoggedInUsersOnly]: __('Secret'),
        [WallAccessPrivacyType.LibraryWideUnlisted]: __('Team only'),
        [WallAccessPrivacyType.LibraryWideListed]: __('Team only'),
      },
    },
    [LibraryType.School]: {
      short: {
        [WallAccessPrivacyType.Public]: __('Public'),
        [WallAccessPrivacyType.Secret]: __('Secret'),
        [WallAccessPrivacyType.Private]: __('Private'),
        [WallAccessPrivacyType.OrgWideUnlisted]: __('Org only'),
        [WallAccessPrivacyType.OrgWideListed]: __('Org only'),
        [WallAccessPrivacyType.PasswordProtected]: __('Password'),
        [WallAccessPrivacyType.LoggedInUsersOnly]: __('Secret'),
        [WallAccessPrivacyType.LibraryWideUnlisted]: __('School only'),
        [WallAccessPrivacyType.LibraryWideListed]: __('School only'),
      },
      long: {
        [WallAccessPrivacyType.Public]: __('Public'),
        [WallAccessPrivacyType.Secret]: __('Secret'),
        [WallAccessPrivacyType.Private]: __('Private'),
        [WallAccessPrivacyType.OrgWideUnlisted]: __('Organization only'),
        [WallAccessPrivacyType.OrgWideListed]: __('Organization only'),
        [WallAccessPrivacyType.PasswordProtected]: __('Password protected'),
        [WallAccessPrivacyType.LoggedInUsersOnly]: __('Secret'),
        [WallAccessPrivacyType.LibraryWideUnlisted]: __('School only'),
        [WallAccessPrivacyType.LibraryWideListed]: __('School only'),
      },
    },
    [LibraryType.Classroom]: {
      short: {
        [WallAccessPrivacyType.Public]: __('Public'),
        [WallAccessPrivacyType.Secret]: __('Secret'),
        [WallAccessPrivacyType.Private]: __('Private'),
        [WallAccessPrivacyType.OrgWideUnlisted]: __('Org only'),
        [WallAccessPrivacyType.OrgWideListed]: __('Org only'),
        [WallAccessPrivacyType.PasswordProtected]: __('Password'),
        [WallAccessPrivacyType.LoggedInUsersOnly]: __('Secret'),
        [WallAccessPrivacyType.LibraryWideUnlisted]: __('Classroom only'),
        [WallAccessPrivacyType.LibraryWideListed]: __('Classroom only'),
      },
      long: {
        [WallAccessPrivacyType.Public]: __('Public'),
        [WallAccessPrivacyType.Secret]: __('Secret'),
        [WallAccessPrivacyType.Private]: __('Private'),
        [WallAccessPrivacyType.OrgWideUnlisted]: __('Organization only'),
        [WallAccessPrivacyType.OrgWideListed]: __('Organization only'),
        [WallAccessPrivacyType.PasswordProtected]: __('Password protected'),
        [WallAccessPrivacyType.LoggedInUsersOnly]: __('Secret'),
        [WallAccessPrivacyType.LibraryWideUnlisted]: __('Classroom only'),
        [WallAccessPrivacyType.LibraryWideListed]: __('Classroom only'),
      },
    },
  }[libraryType][length]
}

const WALL_ACCESS_PRIVACY_TYPE_ICON: { [key in WallAccessPrivacyType]: string } = {
  [WallAccessPrivacyType.Public]: 'public',
  [WallAccessPrivacyType.Secret]: 'visibility_off',
  [WallAccessPrivacyType.OrgWideUnlisted]: 'organization',
  [WallAccessPrivacyType.OrgWideListed]: 'organization',
  [WallAccessPrivacyType.PasswordProtected]: 'key',
  [WallAccessPrivacyType.Private]: 'lock',
  [WallAccessPrivacyType.LoggedInUsersOnly]: 'organization',
  [WallAccessPrivacyType.LibraryWideUnlisted]: 'organization',
  [WallAccessPrivacyType.LibraryWideListed]: 'organization',
}

export {
  WALL_ACCESS_PRIVACY_TYPE_ICON,
  WALL_ACCESS_RIGHT_COLOR_CLASS,
  WALL_ACCESS_RIGHT_MAP,
  WALL_ACCESS_RIGHT_TO_INTEGER_MAP,
  getWallAccessPrivacyTypeName,
  getWallAccessPrivacyTypeText,
}
